import { ApiError, type Api } from "../api/client.js";
import type { BundleView, CardView, MandateView, OccurrenceView, Receipt } from "../api/types.js";
import { ActivityView } from "../ui/activity.js";
import type { AimHandlers } from "../ui/aim.js";
import type { Tab } from "../ui/tabs.js";
import { activityModel, filterFeed, type ActivityFeed } from "./activity-model.js";
import { occurrenceReceipt, receiptModel, type ReceiptContext, type ReceiptModel } from "./receipt-model.js";
import { ReceiptSheets } from "./receipt-sheets.js";
import { scheduledSection } from "./mandates-model.js";

/**
 * The Activity tab's controller: the reads, the model, the filter, the receipts.
 *
 * The ledger is read in one call; each bundle it names is read twice (the signed
 * payload for the words, the receipt for what ran and each operation's before →
 * after) — in parallel, bounded by the ledger page. A bundle whose reads fail
 * falls back to its ledger lines (the model's rule), so one bad read never blanks
 * the statement.
 *
 * A receipt opens as the bank's sheet over the list (the list `aria-hidden` and
 * inert behind it). Verified opens the proof when judge mode is on, else the
 * four-line customer sheet — whose last line is said only when the bank's own
 * check of the trail (`GET /v1/audit/verify`) holds. A held operation can be
 * cancelled from its receipt: a light in-sheet question (06's pattern), then the
 * gateway's one cancel route, then the receipt again as the bank now reads it.
 */
export interface ActivityDeps {
  readonly api: Api;
  readonly host: HTMLElement;
  readonly now: () => number;
  readonly reduced: () => boolean;
  readonly judgeOn: () => boolean;
  readonly onProof: (bundleId: string) => void;
  readonly onTab: (tab: Tab) => void;
  readonly assistant: AimHandlers;
  readonly onSignedOut: () => void;
  /** DCTA-209 (F57): a schedule — a Scheduled row, a "Set up" row, a receipt's schedule row. */
  readonly onSchedule?: ((mandateId: string) => void) | undefined;
  /** DCTA-209 (28a): "See all {n}" — the Scheduled list. */
  readonly onSeeAll?: (() => void) | undefined;
}

const PAGE = 50;

export class ActivityScreen {
  readonly view: ActivityView;
  readonly #d: ActivityDeps;
  #feed: ActivityFeed | undefined;
  #context: ReceiptContext | undefined;
  #selected = "all";
  #seq = 0;
  /** DCTA-229: the receipt sheets (Verified, the held part's Cancel) — shared with the Invest tab. */
  readonly #receipts: ReceiptSheets;
  /**
   * DCTA-143 (§F.2 18f): bundles revealed by id this session (the success sheet's and the card's Receipt). A signature
   * that posted nothing — every step refused or stopped when it ran — has no ledger line to be found by, so it is read
   * beside the ledger's bundles; the model lists it only when nothing of it ran (activity-model.ts).
   */
  readonly #revealed = new Set<string>();
  /** DCTA-209 (F57): route 1 as last read (undefined: not read, or the read failed — no section, no schedule rows). */
  #mandates: readonly MandateView[] | undefined;
  /** DCTA-209 (F57): route 2's skipped payments, per schedule. */
  #skipped = new Map<string, readonly OccurrenceView[]>();
  /** DCTA-209: focus goes back to what opened the Scheduled layer at the next draw (`returnFocus`). */
  #restore = false;

  constructor(deps: ActivityDeps) {
    this.#d = deps;
    this.#receipts = new ReceiptSheets({
      api: deps.api,
      host: deps.host,
      reduced: deps.reduced,
      judgeOn: deps.judgeOn,
      onProof: deps.onProof,
      behind: () => [this.view.topbar, this.view.body, this.view.dock],
    });
    this.view = new ActivityView({
      onOpen: (key) => this.open(key),
      onSeeAll: () => {
        this.view.markFocus();
        deps.onSeeAll?.();
      },
      onFilter: (id) => {
        this.#selected = id;
        this.#paint();
      },
      onRetry: () => void this.refresh(),
      onTab: deps.onTab,
      assistant: deps.assistant,
    });
  }

  /**
   * DCTA-209 (WCAG 2.4.3): back from the Scheduled layer — the next time Activity is drawn (or says it could not read), focus
   * goes back to what opened the layer. A tab taken instead never asks for it: nothing is restored then.
   */
  returnFocus(): void {
    this.#restore = true;
  }

  async refresh(): Promise<void> {
    const seq = ++this.#seq;
    if (this.#feed === undefined) this.view.loading();
    const api = this.#d.api;
    try {
      // DCTA-166: the customer's history is what they signed — the ledger and the bundles it (or a signature) names. The
      // bank's refused drafts are the audit trail's, not read here.
      // DCTA-209 (F57): route 1 beside them — every schedule (the section, the payments' titles, the set-up days). A bank
      // without it (404) or a failed read leaves Activity as it was: no section, the schedules' lines as plain lines.
      const [ledger, accounts, cards, mandates] = await Promise.all([
        api.ledgerEntries(PAGE),
        api.accounts(),
        api.cards().catch(() => ({ cards: [] as readonly CardView[] })),
        (async () => (await api.mandates()).mandates)().catch(() => undefined),
      ]);
      // DCTA-209 (§13.6): a schedule's payment carries `mandate` and no bundle id — never a bundle to read (F60).
      const lined = new Set(ledger.entries.map((e) => e.bundle_id).filter((id): id is string => id !== undefined));
      // A revealed bundle that now has a line is found by its line; it is read once, never twice.
      for (const id of lined) this.#revealed.delete(id);
      const ids = [...lined, ...this.#revealed];
      const bundles = new Map<string, { view: BundleView; receipt: Receipt }>();
      // DCTA-209 (F57): skipped payments are not in the ledger — route 2 for each schedule that skipped one (no new route),
      // read beside the bundles.
      const skipped = new Map<string, readonly OccurrenceView[]>();
      await Promise.all([
        ...ids.map(async (id) => {
          try {
            const [view, receipt] = await Promise.all([api.bundle(id), api.receipt(id)]);
            bundles.set(id, { view, receipt });
          } catch {
            // The ledger's own lines stand in for this bundle.
          }
        }),
        ...(mandates ?? [])
          .filter((m) => Number(m.payments_skipped) > 0)
          .map(async (m) => {
            const d = await api.mandate(m.mandate_id).catch(() => undefined);
            if (d !== undefined) skipped.set(m.mandate_id, d.occurrences.filter((o) => o.state !== "executed"));
          }),
      ]);
      if (seq !== this.#seq) return;
      this.#mandates = mandates;
      this.#skipped = skipped;
      // A revealed signature that ran or waits will post lines: the ledger finds it from now on. DCTA-166: an id the bank
      // does not say was signed is never an entry (the model's rule) — it is not read again either.
      for (const id of this.#revealed) {
        const b = bundles.get(id);
        if (b === undefined) continue;
        if (b.receipt.signed_at === undefined || b.receipt.steps.some((s) => s.state === "executed" || s.state === "held")) this.#revealed.delete(id);
      }
      this.#context = { bundles, entries: ledger.entries, accounts: accounts.accounts, cards: cards.cards };
      this.#feed = activityModel({ ...this.#context, now: this.#d.now(), ...(mandates === undefined ? {} : { mandates, skipped }) });
      this.#paint();
    } catch (error) {
      if (seq !== this.#seq) return;
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      this.view.unreachable();
      this.#returned();
    }
  }

  /**
   * The success sheet's Receipt pill (P2-1): Activity, read fresh, at this bundle — a
   * one-step bundle opens its receipt; a bundle opens in place, its operations' receipts
   * one tap away, and comes into view.
   */
  async reveal(bundleId: string): Promise<void> {
    this.#selected = "all";
    this.#revealed.add(bundleId);
    await this.refresh();
    const row = this.#feed?.days.flatMap((d) => d.rows).find((r) => (r.kind === "bundle" || r.kind === "single") && r.id === bundleId);
    if (row === undefined) return;
    if (row.kind === "single") {
      this.open(row.op.key);
      return;
    }
    if (row.kind !== "bundle") return;
    this.view.expand(row.id);
    this.#paint();
    this.view.scrollToBundle(row.id);
  }

  /** "See all in Activity" from an account's detail (§C.5): Activity, read fresh, filtered to it. */
  async showAccount(account: string): Promise<void> {
    this.#selected = account;
    await this.refresh();
  }

  /** A receipt, by the key its row carries — or (DCTA-209) a schedule (`mandate:{id}`). */
  open(key: string): void {
    const ctx = this.#context;
    if (ctx === undefined || this.#receipts.isOpen) return;
    if (key.startsWith("mandate:")) {
      this.view.markFocus();
      this.#d.onSchedule?.(key.slice("mandate:".length));
      return;
    }
    const m = key.startsWith("occ:") ? this.#occurrence(key) : receiptModel(key, ctx);
    if (m === undefined) return;
    this.#receipts.open({
      model: m,
      context: () => this.#context,
      refresh: () => this.refresh(),
      reopen: () => this.open(key),
      // DCTA-209 (30b, 30c): the schedule row — the receipt closes, the schedule opens.
      ...(this.#d.onSchedule === undefined
        ? {}
        : {
            onSchedule: (id: string) => {
              this.view.markFocus();
              this.#d.onSchedule?.(id);
            },
          }),
    });
  }

  /** DCTA-209 (F60): `occ:{mandate_id}:{seq}` — a payment that ran (its ledger line) or one route 2 says was skipped. */
  #occurrence(key: string): ReceiptModel | undefined {
    const [, id, seq] = key.split(":");
    const m = this.#mandates?.find((x) => x.mandate_id === id);
    const ctx = this.#context;
    if (m === undefined || ctx === undefined || seq === undefined) return undefined;
    const skipped = this.#skipped.get(m.mandate_id)?.find((o) => o.seq === seq);
    if (skipped !== undefined) return occurrenceReceipt({ mandate: m, occurrence: skipped });
    const lines = ctx.entries.filter((e) => e.mandate?.mandate_id === m.mandate_id && e.mandate.occurrence === seq);
    const entry = lines.find((e) => e.direction === "debit") ?? lines[0];
    return entry === undefined ? undefined : occurrenceReceipt({ mandate: m, entry });
  }

  #paint(): void {
    if (this.#feed === undefined) return;
    // DCTA-209 (F57): the Scheduled section first (an account filter keeps the schedules that take from it), then the days.
    this.view.scheduled(this.#mandates === undefined ? undefined : scheduledSection(this.#mandates, this.#selected));
    this.view.show(filterFeed(this.#feed, this.#selected, this.#d.now()), this.#selected);
    this.#returned();
  }

  #returned(): void {
    if (!this.#restore) return;
    this.#restore = false;
    this.view.returnFocus();
  }
}
